// The document behind a statement figure, for one vehicle.
//
// Luyanda, 6 September 2026: the insurance and Cartrack schedules should be
// clickable from a figure, "redact for everyone else for that month, so in a month
// we have 30 cars, that one thing would have thirty copies with varying redaction".
//
// The obvious way to build that is to redact the shared PDF thirty times. This is
// not that. The extract is assembled from the vehicle's own rows, so no other
// owner's figures were ever in it to remove. That is why the redaction line at the
// bottom says "was never in it" rather than "has been removed": those are
// different promises, and only one of them survives somebody making a mistake.
import { useEffect } from 'react'
import { FileText, ShieldCheck, CircleAlert, CircleCheck } from 'lucide-react'
import { useGetEvidenceExtract } from '../../hooks/backend/statements'
import { Spinner } from '../../lib/shadcn/spinner'
import { formatZar } from '../../utils/statements'
import { cn } from '../../lib/shadcn/utils'

export type EvidenceExtract = {
  id: string
  plate: string
  period_start: string
  line_code: string
  title: string
  rows: Array<Record<string, unknown>>
  row_count: number
  total_zar: number | null
  basis_note: string | null
  redaction_note: string | null
  supplier: string | null
  document_number: string | null
  gmail_thread_id: string | null
  source_covers_vehicles: number | null
  built_at: string
}

// Column order per line code, because a generic key dump reads as debug output.
// Anything not listed still renders, after these, so a new field in the extract
// shows up rather than silently disappearing.
const COLUMNS: Record<string, string[]> = {
  earnings: ['shift_date', 'shift_window', 'recorded_zar', 'uber_zar', 'variance_zar', 'validated_by_uber'],
  petrol: ['shift_date', 'kilometres', 'km_per_litre', 'litres_used', 'price_per_litre_zar', 'petrol_cost_zar'],
  insurance: ['supplier', 'document_number', 'description', 'amount_incl_vat_zar'],
  telemetric_system: ['supplier', 'document_number', 'description', 'amount_incl_vat_zar'],
}

const HEADING: Record<string, string> = {
  shift_date: 'Date',
  shift_window: 'Shift',
  recorded_zar: 'We recorded',
  uber_zar: 'Uber says',
  variance_zar: 'Difference',
  validated_by_uber: 'Uber',
  kilometres: 'KM',
  km_per_litre: 'KM/L',
  litres_used: 'Litres',
  price_per_litre_zar: 'Price/L',
  petrol_cost_zar: 'Petrol',
  supplier: 'Supplier',
  document_number: 'Document',
  description: 'Description',
  amount_incl_vat_zar: 'Amount incl VAT',
}

const MONEY = new Set([
  'recorded_zar', 'uber_zar', 'variance_zar', 'petrol_cost_zar',
  'price_per_litre_zar', 'amount_incl_vat_zar',
])

function cell(key: string, value: unknown) {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>
  if (key === 'validated_by_uber') {
    return value === true ? (
      <CircleCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
    ) : (
      <CircleAlert className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
    )
  }
  if (MONEY.has(key)) {
    const n = Number(value)
    if (!Number.isFinite(n)) return String(value)
    return <span className="tabular-nums">{formatZar(n)}</span>
  }
  if (typeof value === 'number') return <span className="tabular-nums">{value}</span>
  return String(value)
}

export function EvidenceExtractView({ token }: { token: string }) {
  const query = useGetEvidenceExtract<EvidenceExtract | null>()
  const { trigger } = query

  useEffect(() => {
    if (!token) return
    trigger({ token }).catch(() => {})
  }, [trigger, token])

  if (query.loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Opening the document
      </div>
    )
  }
  // A read that failed is not the same as a figure with nothing behind it.
  if (query.error) {
    return (
      <div className="py-3 text-sm text-muted-foreground">
        Could not open the document just now. That is not the same as there being
        none.
      </div>
    )
  }

  const e = query.data
  if (!e) return <div className="py-3 text-sm text-muted-foreground">No document held for this figure.</div>

  const keys = COLUMNS[e.line_code] ?? Object.keys(e.rows[0] ?? {})
  const extras = Object.keys(e.rows[0] ?? {}).filter((k) => !keys.includes(k))
  const cols = [...keys, ...extras]

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex items-start gap-2">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <div className="font-semibold">{e.title}</div>
          {e.basis_note && <p className="mt-0.5 text-xs text-muted-foreground">{e.basis_note}</p>}
        </div>
      </div>

      <div className="max-h-72 overflow-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/60">
            <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {cols.map((k) => (
                <th key={k} className={cn('px-2 py-1.5 font-semibold', MONEY.has(k) ? 'text-right' : 'text-left')}>
                  {HEADING[k] ?? k.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {e.rows.map((r, i) => (
              <tr key={i} className="border-t border-border/60">
                {cols.map((k) => (
                  <td key={k} className={cn('px-2 py-1', MONEY.has(k) ? 'text-right' : 'text-left')}>
                    {cell(k, r[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {e.total_zar !== null && (
            <tfoot>
              <tr className="border-t border-border bg-muted/40 font-semibold">
                <td className="px-2 py-1.5" colSpan={Math.max(1, cols.length - 1)}>
                  {e.row_count} line{e.row_count === 1 ? '' : 's'}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{formatZar(e.total_zar)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* The promise that matters, said in the words that are actually true. */}
      {e.redaction_note && (
        <div className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span>{e.redaction_note}</span>
        </div>
      )}

      {e.gmail_thread_id && (
        <a
          className="text-xs font-medium text-primary underline underline-offset-2"
          href={`https://mail.google.com/mail/u/0/#all/${e.gmail_thread_id}`}
          target="_blank"
          rel="noreferrer"
        >
          Open the email the schedule arrived on (internal: the full schedule covers
          {e.source_covers_vehicles ? ` ${e.source_covers_vehicles} vehicles` : ' more than this one'})
        </a>
      )}
    </div>
  )
}
